import DOMPurify from 'dompurify';
import { useT } from '../../i18n';
import type { CommentRow } from '../../data/dto';

interface Props {
  comments: CommentRow[];
  currentUserId: string | null;
  /** True when the current user owns the baby (may delete any comment). */
  isOwner: boolean;
  onDelete: (commentId: string) => void;
  deletingId?: string | null;
}

/**
 * Render stored body_html with DOMPurify sanitization.
 * Only allows safe formatting tags (<b>, <strong>, <i>, <em>, <br>) and strips all
 * attributes and dangerous tags to prevent XSS. Converts newlines into <br>.
 */
export function renderBody(html: string): string {
  if (!html) return '';
  const withLineBreaks = html.replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
  return DOMPurify.sanitize(withLineBreaks, {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'br'],
    ALLOWED_ATTR: [],
  });
}

function relativeTime(utc: string, t: ReturnType<typeof useT>): string {
  // Stored as 'YYYY-MM-DD HH:MM:SS' in UTC.
  const ms = Date.parse(utc.replace(' ', 'T') + 'Z');
  if (Number.isNaN(ms)) return '';
  const diff = Math.max(0, Date.now() - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return t('engagement.justNow', 'just now');
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(ms).toLocaleDateString();
}

function initials(name: string | null): string {
  if (!name) return '·';
  const cleanName = name.replace(/\s*\([^)]*\)/g, '').trim();
  const parts = cleanName.split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '·';
}

export function CommentList({ comments, currentUserId, isOwner, onDelete, deletingId }: Props) {
  const t = useT();
  if (comments.length === 0) return null;

  return (
    <ul className="flex flex-col gap-3">
      {comments.map((c) => {
        const canDelete = isOwner || c.user_id === currentUserId;
        return (
          <li key={c.id} className="flex gap-2.5">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-grey-soft text-[11px] font-semibold text-ink-2">
              {initials(c.author_name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="truncate text-[13px] font-semibold text-ink">
                  {c.author_name ?? t('engagement.someone', 'Someone')}
                </span>
                <span className="shrink-0 text-[11.5px] text-ink-3">{relativeTime(c.created_at, t)}</span>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(c.id)}
                    disabled={deletingId === c.id}
                    className="ml-auto shrink-0 text-[11.5px] text-ink-3 hover:text-ink-2 disabled:opacity-40"
                  >
                    {t('engagement.delete', 'Delete')}
                  </button>
                )}
              </div>
              <div
                className="mt-0.5 break-words text-[14px] leading-relaxed text-ink-2 [&_b]:font-semibold [&_b]:text-ink [&_strong]:font-semibold [&_strong]:text-ink [&_i]:italic [&_em]:italic"
                dangerouslySetInnerHTML={{ __html: renderBody(c.body_html) }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
